/**
 * Photo Geo-Tagging
 * Extracts GPS coordinates from EXIF data or uses device location
 */

import * as exifr from 'exifr'
import type { GeoLocation } from '../types'

export interface PhotoMetadata {
  location?: GeoLocation
  timestamp?: Date
  orientation?: number
  make?: string
  model?: string
}

/**
 * Extract GPS and metadata from photo EXIF
 */
export async function extractPhotoMetadata(
  imageSource: File | Blob
): Promise<PhotoMetadata> {
  try {
    const exif = await exifr.parse(imageSource, {
      gps: true,
      tiff: true,
      exif: true,
    })

    if (!exif) {
      return {}
    }

    const metadata: PhotoMetadata = {}

    // Extract GPS coordinates
    if (exif.latitude !== undefined && exif.longitude !== undefined) {
      metadata.location = {
        latitude: exif.latitude,
        longitude: exif.longitude,
        accuracy: exif.GPSHPositioningError || undefined,
      }
    }

    // Extract timestamp
    if (exif.DateTimeOriginal) {
      metadata.timestamp = new Date(exif.DateTimeOriginal)
    } else if (exif.CreateDate) {
      metadata.timestamp = new Date(exif.CreateDate)
    }

    // Extract orientation for display
    metadata.orientation = exif.Orientation

    // Extract device info
    metadata.make = exif.Make
    metadata.model = exif.Model

    return metadata
  } catch (error) {
    console.error('[GeoTag] Failed to extract EXIF:', error)
    return {}
  }
}

/**
 * Get current device location as fallback
 */
export async function getCurrentLocation(): Promise<GeoLocation | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp),
        })
      },
      (error) => {
        console.error('[GeoTag] Geolocation error:', error)
        resolve(null)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    )
  })
}

/**
 * Get location for a photo, preferring EXIF, falling back to device GPS
 */
export async function getPhotoLocation(
  imageSource: File | Blob,
  fallbackToDeviceGps: boolean = true
): Promise<GeoLocation | null> {
  // Try EXIF first
  const metadata = await extractPhotoMetadata(imageSource)

  if (metadata.location) {
    return metadata.location
  }

  // Fallback to device GPS
  if (fallbackToDeviceGps) {
    return getCurrentLocation()
  }

  return null
}

/**
 * Compress image for storage/transmission
 */
export async function compressPhoto(
  imageSource: File | Blob,
  maxWidth: number = 1200,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      // Calculate new dimensions
      let width = img.width
      let height = img.height

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width)
        width = maxWidth
      }

      canvas.width = width
      canvas.height = height

      // Draw compressed image
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to compress image'))
          }
        },
        'image/jpeg',
        quality
      )
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    img.src = URL.createObjectURL(imageSource)
  })
}

/**
 * Create a thumbnail for preview
 */
export async function createThumbnail(
  imageSource: File | Blob,
  maxSize: number = 200
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        reject(new Error('Canvas context not available'))
        return
      }

      // Calculate thumbnail dimensions
      let width = img.width
      let height = img.height

      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width)
          width = maxSize
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height)
          height = maxSize
        }
      }

      canvas.width = width
      canvas.height = height

      ctx.drawImage(img, 0, 0, width, height)

      resolve(canvas.toDataURL('image/jpeg', 0.7))
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    img.src = URL.createObjectURL(imageSource)
  })
}
